import { Expand, Ruler as RulerIcon, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  CARD_WIDTH_MM,
  DEFAULT_PX_PER_MM,
  MM_PER_INCH,
  type RulerUnit,
} from "@/lib/measure/ruler";
import { m } from "@/paraglide/messages";
import { RulerStrip } from "./RulerStrip";

const PX_KEY = "ruler_pxPerMm";
const UNIT_KEY = "ruler_unit";
const UNITS: RulerUnit[] = ["cm", "mm", "inch"];

function UnitPicker({
  unit,
  onChange,
}: {
  unit: RulerUnit;
  onChange: (u: RulerUnit) => void;
}) {
  return (
    <div className="inline-flex gap-1 rounded-lg border border-border bg-card/70 p-1">
      {UNITS.map((u) => (
        <button
          key={u}
          type="button"
          aria-pressed={unit === u}
          onClick={() => onChange(u)}
          className={
            unit === u
              ? "rounded-md bg-primary px-3 py-1 text-sm font-semibold text-primary-foreground"
              : "rounded-md px-3 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          }
        >
          {u}
        </button>
      ))}
    </div>
  );
}

export function RulerCalc() {
  const [pxPerMm, setPxPerMm] = useState(DEFAULT_PX_PER_MM);
  const [unit, setUnit] = useState<RulerUnit>("cm");
  const [stageW, setStageW] = useState(0);
  const [vp, setVp] = useState({ w: 0, h: 0 });
  const [showCal, setShowCal] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  // Load persisted calibration + unit.
  useEffect(() => {
    const p = Number(localStorage.getItem(PX_KEY));
    if (p > 0) setPxPerMm(p);
    const u = localStorage.getItem(UNIT_KEY);
    if (u === "cm" || u === "mm" || u === "inch") setUnit(u);
  }, []);
  useEffect(() => {
    if (pxPerMm > 0) localStorage.setItem(PX_KEY, String(pxPerMm));
  }, [pxPerMm]);
  useEffect(() => {
    localStorage.setItem(UNIT_KEY, unit);
  }, [unit]);

  // Track viewport + stage width.
  useEffect(() => {
    const measure = () => {
      setVp({ w: window.innerWidth, h: window.innerHeight });
      if (stageRef.current) setStageW(stageRef.current.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  const portrait = vp.h > vp.w;
  const cardW = pxPerMm * CARD_WIDTH_MM;
  const dpi = Math.round(pxPerMm * MM_PER_INCH);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <div className="flex flex-wrap items-center gap-3">
        <UnitPicker unit={unit} onChange={setUnit} />
        <button
          type="button"
          onClick={() => setShowCal((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card/70 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {m.ruler_calibrate()}
        </button>
        <button
          type="button"
          onClick={() => setFullscreen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card/70 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <Expand className="h-4 w-4" />
          {m.ruler_fullscreen()}
        </button>
      </div>

      {showCal && (
        <div className="sci-panel mt-4 rounded-xl p-4">
          <p className="text-sm text-muted-foreground">
            {m.ruler_calibrate_help()}
          </p>
          <div className="mt-3 overflow-hidden">
            {/* Live credit-card outline — resize until it matches a real card. */}
            <div
              className="flex items-center justify-center rounded-lg border-2 border-dashed border-primary/60 bg-primary/5 text-xs text-muted-foreground"
              style={{
                width: `${cardW}px`,
                height: `${(cardW / CARD_WIDTH_MM) * 53.98}px`,
              }}
            >
              85.6 × 53.98 mm
            </div>
          </div>
          <input
            type="range"
            min={2.5}
            max={7}
            step={0.01}
            value={pxPerMm}
            onChange={(e) => setPxPerMm(Number(e.target.value))}
            aria-label={m.ruler_calibrate()}
            className="mt-4 w-full accent-[var(--accent-c)]"
          />
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{m.ruler_dpi({ dpi })}</span>
            <button
              type="button"
              onClick={() => setPxPerMm(DEFAULT_PX_PER_MM)}
              className="underline transition-colors hover:text-foreground"
            >
              {m.ruler_reset()}
            </button>
          </div>
        </div>
      )}

      {/* In-page ruler — spans the card width. */}
      <div
        ref={stageRef}
        className="mt-5 overflow-hidden rounded-lg border border-border bg-card/40"
      >
        {stageW > 0 && (
          <RulerStrip
            length={stageW}
            pxPerMm={pxPerMm}
            unit={unit}
            orientation="horizontal"
          />
        )}
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <RulerIcon className="h-3.5 w-3.5" />
        {m.ruler_hint()}
      </p>

      {fullscreen && (
        <div className="fixed inset-0 z-[60] bg-background">
          {/* Ruler pinned to the real screen edge (long edge). */}
          {portrait ? (
            <div className="absolute left-0 top-0 h-full">
              <RulerStrip
                length={vp.h}
                pxPerMm={pxPerMm}
                unit={unit}
                orientation="vertical"
              />
            </div>
          ) : (
            <div className="absolute left-0 top-0 w-full">
              <RulerStrip
                length={vp.w}
                pxPerMm={pxPerMm}
                unit={unit}
                orientation="horizontal"
              />
            </div>
          )}
          {/* Centered controls */}
          <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3">
            <UnitPicker unit={unit} onChange={setUnit} />
            <p className="max-w-xs text-center text-xs text-muted-foreground">
              {m.ruler_edge_hint()}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            aria-label={m.ruler_exit()}
            className="absolute right-4 top-4 rounded-full border border-border bg-card p-2 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
